import { isCustomerLoggedIn } from '@raisin/cp-utils';
import { productTypes } from 'types';

type URLConfig = {
  base: string;
  offers: string;
  wpOffers: string;
};

type Prolongation = {
  show?: boolean;
  hideForOvernight?: boolean;
};

type OfferDetails = {
  showCompounding?: boolean;
  showInterestBooking?: boolean;
  showInterestBookingOnlyForOvernight?: boolean;
  showInterestTaxation?: boolean;
  showInterestType?: boolean;
  showInterestPayout?: boolean;
  showInterestPayment?: boolean;
  showInterestEarlyTermination?: boolean;
  showInterestTypeInAER?: boolean;
  showImportantInformation?: boolean;
  showSpecialFeatures?: boolean;
  showCosts?: boolean;
  showWithholdingTax?: boolean;
  showAdditionalDocuments?: boolean;
  showCurrency?: boolean;
  showEffectiveInterestRate?: boolean;
  showNominalInterestRate?: boolean;
  prolongation?: Prolongation;
  showTermsAndConditionsLink?: Boolean;
  showProductInformationPDFLink?: boolean;
  showRepresentativeExample?: boolean;
  showAvailability?: boolean;
  showTermFieldForNotice?: boolean;
};

export type OffersTableConfig = {
  url: Partial<URLConfig>;
  defaultRate: string;
  filter: {
    term?: {
      min: number;
      max: number;
    };
    type: string;
    currency: string;
    investmentAmount: number;
    countryCreditRating: string;
    showForeignCurrency?: boolean;
    showOnlyFinanztestBestenlisteProducts?: boolean;
  };
  features: {
    showProductDetails?: boolean;
    showFilterComponent?: boolean;
    showTermFilter?: boolean;
    showCountryCreditRatingFilter?: boolean;
    showInvestmentAmountFilter?: boolean;
    showFlexgeldFilter?: boolean;
    showForeignCurrencyFilter?: boolean;
    showBankTab?: boolean;
    showChip?: boolean;
    showBankInfo?: boolean;
    showRibbon?: boolean;
    showRiskBanner?: boolean;
    showDepositGuaranteeTab?: boolean;
    showDepositProtection?: boolean;
    showVariableInterestLabel?: boolean;
    splitNoticeAccountFromOvernightOffers?: boolean;
    showCTAButtons?: boolean;
    showCountryCreditRating: boolean;
    showFullOnlineProcessChip?: boolean;
    showFlexgeldChip?: boolean;
    showFinanztestBestenListeChip?: boolean;
    showNoWithholdingTaxChip?: boolean;
    showIsRateReducingSoonChip?: boolean;
    showAlternativeRateDecreaseTooltipForOvernight?: boolean;
    showSpecialNoticeAccountAsOvernight?: boolean;
    offerDetails: OfferDetails;
    showInterestEarned: boolean;
    showDecimalPlacesForInterestAndBonus?: boolean;
    showBonus?: boolean;
    showDepositGuaranteeHeading?: boolean;
    showNoForeignTaxChip?: boolean;
    showAutomaticTaxHandlingChip?: boolean;
    showTermFieldForNotice?: boolean;
    showSustainableChip?: boolean;
  };
  order?: {
    direction: string;
    byKey: string;
  };
};

export type CountryConfig = {
  BUTTON_LINK_CTA_MOBILE?: string;
  BUTTON_LINK_CTA_DESKTOP?: string;
  defaultRate?: string | number;
  environmentCurrency?: string;
  environment?: string;
  showEasyAccessLabel?: boolean;
  features: {
    showFlexgeldFilter?: boolean;
    showVariableInterestLabel?: boolean;
    showDepositProtection?: boolean;
    showDepositGuaranteeHeading?: boolean;
    showRibbon?: boolean;
    showNoWithholdingTaxChip?: boolean;
    showNoForeignTaxChip?: boolean;
    showAutomaticTaxHandlingChip?: boolean;
    showIsRateReducingSoonChip?: boolean;
    showSpecialNoticeAccountAsOvernight?: boolean;
    offerDetails?: OfferDetails;
    showRiskBanner?: boolean;
    showFullOnlineProcessChip?: boolean;
    showDecimalPlacesForInterestAndBonus?: boolean;
    showFlexgeldChip?: boolean;
    showFinanztestBestenListeChip?: boolean;
    showAlternativeRateDecreaseTooltipForOvernight?: boolean;
    showTermFieldForNotice?: boolean;
    showSustainableChip?: boolean;
  };
  filter?: {
    currency?: string;
    term?: { min: number; max: number };
    investmentAmount?: number;
  };
};

export type SortingType = {
  direction: 'asc' | 'desc';
  byKey: 'rate' | 'bank' | 'country' | 'term' | 'noticePeriod';
};

export type Config = {
  BUTTON_LINK_CTA_MOBILE: string;
  BUTTON_LINK_CTA_DESKTOP: string;
  url: URLConfig;
  offersTableConfig: OffersTableConfig;
  i18n: {
    currency: string;
  };
  ENABLE_TABS: boolean;
  CUSTOMER_GROUP: string;
  environment: string;
  environmentCurrency: string;
  DEFAULT_INVESTMENT_TERM: number;
  GBRConfig: CountryConfig;
  DEUConfig: CountryConfig;
  AUTConfig: CountryConfig;
  ESPConfig: CountryConfig;
  NLDConfig: CountryConfig;
  FRAConfig: CountryConfig;
  IRLConfig: CountryConfig;
  POLConfig: CountryConfig;
  FINConfig: CountryConfig;
  showBonus?: boolean;
  order?: SortingType;
};

const EFFECTIVE_INTEREST_RATE = 'effective_interest_rate';
const NOMINAL_INTEREST_RATE = 'nominal_interest_rate';

export const sortingTypes = {
  rate: 'rate',
  bank: 'bank',
  country: 'country',
  term: 'term',
  noticePeriod: 'noticePeriod',
};

export const sortingDirections = {
  desc: 'desc',
  asc: 'asc',
};

/*********************************************************************************************************************************************

    DEFAULT CONFIG FOR OFFERS TABLE. Avoid making any country specific changes in here, because it will break the other offer tables on production.
    Always use country specific configs which starts from [country_code]Config.

**********************************************************************************************************************************************/

export const config: Config = {
  BUTTON_LINK_CTA_MOBILE: 'retail_register',
  BUTTON_LINK_CTA_DESKTOP: 'retail_register',
  url: {
    base: '/',
    offers: '/dp/public/v1/offers',
    wpOffers: '/dp/public/v1/wp/deposit/redirect/',
  },
  offersTableConfig: {
    url: {
      base: '/',
    },
    defaultRate: NOMINAL_INTEREST_RATE,
    filter: {
      term: { min: 0, max: 120 },
      type: productTypes.TERM_DEPOSIT,
      currency: 'EUR',
      investmentAmount: 50000,
      countryCreditRating: 'GOOD_AND_HIGHER',
      showForeignCurrency: false,
      showOnlyFinanztestBestenlisteProducts: false,
    },
    features: {
      showProductDetails: true,
      showFilterComponent: true,
      showTermFilter: true,
      showCountryCreditRatingFilter: false,
      showFlexgeldFilter: true,
      showForeignCurrencyFilter: false,
      showChip: true,
      showRibbon: false,
      showBankInfo: false,
      showRiskBanner: false,
      showInvestmentAmountFilter: true,
      showDepositProtection: false,
      showVariableInterestLabel: false,
      showBankTab: true,
      showDepositGuaranteeTab: true,
      showCountryCreditRating: true,
      splitNoticeAccountFromOvernightOffers: false,
      showFullOnlineProcessChip: false,
      showFlexgeldChip: false,
      showFinanztestBestenListeChip: false,
      showNoWithholdingTaxChip: true,
      showNoForeignTaxChip: true,
      showAutomaticTaxHandlingChip: true,
      showIsRateReducingSoonChip: false,
      showSustainableChip: true,
      showAlternativeRateDecreaseTooltipForOvernight: false,
      showSpecialNoticeAccountAsOvernight: true,
      showCTAButtons: true,
      offerDetails: {
        showCompounding: true,
        showInterestBooking: true,
        showInterestBookingOnlyForOvernight: false,
        showInterestTaxation: false,
        showInterestType: true,
        showInterestPayout: false,
        showInterestEarlyTermination: true,
        showInterestPayment: true,
        showInterestTypeInAER: false,
        showImportantInformation: false,
        showRepresentativeExample: false,
        showSpecialFeatures: true,
        showCosts: true,
        showWithholdingTax: true,
        showAdditionalDocuments: true,
        showCurrency: true,
        showEffectiveInterestRate: true,
        showNominalInterestRate: true,
        prolongation: {
          show: true,
          hideForOvernight: false,
        },
        showTermsAndConditionsLink: false,
        showProductInformationPDFLink: true,
        showAvailability: true,
        showTermFieldForNotice: false,
      },
      showInterestEarned: true,
      showDecimalPlacesForInterestAndBonus: false,
      showBonus: true,
      showDepositGuaranteeHeading: true,
    },
    order: {
      direction: sortingDirections.desc,
      byKey: sortingTypes.rate,
    },
  },
  i18n: {
    currency: '',
  },
  ENABLE_TABS: false,
  CUSTOMER_GROUP: 'retail',
  environment: 'COM',
  environmentCurrency: 'EUR',
  DEFAULT_INVESTMENT_TERM: 0,
  GBRConfig: {
    defaultRate: EFFECTIVE_INTEREST_RATE,
    environment: 'GBR',
    BUTTON_LINK_CTA_MOBILE: 'product_details',
    BUTTON_LINK_CTA_DESKTOP: 'product_details',
    showEasyAccessLabel: true,
    environmentCurrency: 'GBP',
    features: {
      showFlexgeldFilter: false,
      showVariableInterestLabel: true,
      showIsRateReducingSoonChip: true,
      showDepositProtection: true,
      showDepositGuaranteeHeading: false,
      showRibbon: true,
      showNoWithholdingTaxChip: false,
      showFinanztestBestenListeChip: false,
      showSpecialNoticeAccountAsOvernight: false,
      showDecimalPlacesForInterestAndBonus: true,
      showNoForeignTaxChip: false,
      showAutomaticTaxHandlingChip: false,
      offerDetails: {
        showAvailability: true,
        showInterestEarlyTermination: false,
        showImportantInformation: true,
        showAdditionalDocuments: false,
        showSpecialFeatures: false,
        showCosts: false,
        showInterestBooking: false,
        showInterestTypeInAER: true,
        showCompounding: true,
        showInterestTaxation: false,
        showInterestType: true,
        showInterestPayout: false,
        showWithholdingTax: false,
        showCurrency: false,
        showEffectiveInterestRate: true,
        showNominalInterestRate: false,
        prolongation: {
          show: true,
          hideForOvernight: true,
        },
      },
    },
    filter: {
      currency: 'GBP',
    },
  },
  // COUNTRY CONFIGS TO OVERRIDE THE DEFAULT CONFIG BEHAVIOUR.
  DEUConfig: {
    defaultRate: NOMINAL_INTEREST_RATE,
    showEasyAccessLabel: false,
    features: {
      showFlexgeldChip: true,
      showFinanztestBestenListeChip: true,
      showAlternativeRateDecreaseTooltipForOvernight: true,
      showNoWithholdingTaxChip: true,
      offerDetails: {
        showAvailability: true,
        showCompounding: true,
        showInterestBooking: true,
        showInterestBookingOnlyForOvernight: false,
        showInterestTaxation: false,
        showInterestType: true,
        showInterestPayout: false,
        showInterestPayment: true,
        showInterestEarlyTermination: true,
        showInterestTypeInAER: false,
        showImportantInformation: false,
        showRepresentativeExample: false,
        showSpecialFeatures: true,
        showCosts: true,
        showWithholdingTax: true,
        showAdditionalDocuments: true,
        showCurrency: true,
        showEffectiveInterestRate: true,
        showNominalInterestRate: true,
        prolongation: {
          show: true,
          hideForOvernight: false,
        },
      },
    },
  },
  AUTConfig: {
    showEasyAccessLabel: false,
    features: {
      showFlexgeldChip: true,
      showNoWithholdingTaxChip: true,
      offerDetails: {
        showAvailability: true,
        showCosts: true,
        showAdditionalDocuments: true,
        showCompounding: true,
        showInterestBooking: true,
        showInterestTaxation: false,
        showInterestType: true,
        showInterestPayout: false,
        showInterestPayment: true,
        showInterestEarlyTermination: true,
        showWithholdingTax: true,
        showEffectiveInterestRate: true,
        showNominalInterestRate: true,
        prolongation: {
          show: true,
          hideForOvernight: false,
        },
        showProductInformationPDFLink: isCustomerLoggedIn() ? true : false,
      },
    },
  },
  ESPConfig: {
    defaultRate: EFFECTIVE_INTEREST_RATE,
    showEasyAccessLabel: false,
    features: {
      showRibbon: true,
      showRiskBanner: true,
      showFullOnlineProcessChip: true,
      showFlexgeldChip: true,
      offerDetails: {
        showAvailability: true,
        showAdditionalDocuments: true,
        showCompounding: true,
        showCosts: true,
        showInterestBooking: true,
        showInterestTaxation: false,
        showInterestType: true,
        showInterestPayout: false,
        showInterestPayment: false,
        showInterestEarlyTermination: true,
        showRepresentativeExample: true,
        showWithholdingTax: true,
        showEffectiveInterestRate: true,
        showNominalInterestRate: true,
        prolongation: {
          show: true,
          hideForOvernight: false,
        },
        showProductInformationPDFLink: true,
      },
      showNoWithholdingTaxChip: true,
      showSpecialNoticeAccountAsOvernight: false,
    },
    filter: {
      currency: 'EUR',
      investmentAmount: 30000,
    },
  },
  NLDConfig: {
    showEasyAccessLabel: false,
    features: {
      showFlexgeldChip: true,
      showNoWithholdingTaxChip: true,
      showSpecialNoticeAccountAsOvernight: false,
      offerDetails: {
        showAvailability: true,
        showCosts: true,
        showAdditionalDocuments: true,
        showInterestBookingOnlyForOvernight: true,
        showCompounding: true,
        showInterestTaxation: false,
        showInterestType: true,
        showInterestPayout: false,
        showInterestPayment: true,
        showInterestEarlyTermination: true,
        showWithholdingTax: true,
        showNominalInterestRate: true,
        showTermFieldForNotice: false,
        prolongation: {
          show: true,
          hideForOvernight: false,
        },
      },
    },
  },
  FRAConfig: {
    showEasyAccessLabel: false,
    defaultRate: EFFECTIVE_INTEREST_RATE,
    features: {
      showFlexgeldChip: true,
      showNoWithholdingTaxChip: true,
    },
    filter: {},
  },
  IRLConfig: {
    showEasyAccessLabel: false,
    defaultRate: EFFECTIVE_INTEREST_RATE,
    features: {
      showNoWithholdingTaxChip: true,
      showSpecialNoticeAccountAsOvernight: false,
    },
    filter: {
      investmentAmount: 30000,
    },
  },
  POLConfig: {
    environmentCurrency: 'zł',
    defaultRate: NOMINAL_INTEREST_RATE,
    showEasyAccessLabel: false,
    features: {
      showFlexgeldChip: false,
      showFlexgeldFilter: false,
      showNoWithholdingTaxChip: false,
      showDecimalPlacesForInterestAndBonus: true,
      showNoForeignTaxChip: false,
      showAutomaticTaxHandlingChip: true,
      offerDetails: {
        showTermsAndConditionsLink: true,
        showNominalInterestRate: true,
        showWithholdingTax: false,
        showAvailability: true,
        showInterestBookingOnlyForOvernight: true,
      },
    },
    filter: {
      currency: 'PLN',
    },
  },
  FINConfig: {
    environmentCurrency: 'EUR',
    defaultRate: NOMINAL_INTEREST_RATE,
    showEasyAccessLabel: false,
    features: {
      showFlexgeldChip: false,
      showFlexgeldFilter: false,
      showNoWithholdingTaxChip: false,
      showDecimalPlacesForInterestAndBonus: true,
      showNoForeignTaxChip: false,
      showAutomaticTaxHandlingChip: false,
      offerDetails: {
        showAvailability: true,
        showCosts: true,
        showAdditionalDocuments: true,
        showInterestBookingOnlyForOvernight: true,
        showCompounding: true,
        showInterestTaxation: false,
        showInterestType: true,
        showInterestPayout: false,
        showInterestPayment: true,
        showInterestEarlyTermination: true,
        showWithholdingTax: true,
        showNominalInterestRate: true,
        showTermFieldForNotice: false,
        prolongation: {
          show: true,
          hideForOvernight: true,
        },
      },
    },
    filter: {},
  },
};
